import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import Alert from 'react-bootstrap/Alert';
import Button from '@components/smartAdminUI/Button';
import useInterval from '@hooks/useInterval';

const AutoSaveMessage = ({
  isAdmin,
  recordIsLocked,
  checkStatus = () => null,
  onTakeover = () => null,
  onDiscard = () => null,
  displayActions = true,
  lastUpdatedOn = null,
  lastCheckedOn = null,
}) => {
  const [minutesElapsed, setMinutesElapsed] = useState(null);
  const canTakeAction = useMemo(() => minutesElapsed >= 5, [minutesElapsed]);
  const lastUpdate = useMemo(() => lastUpdatedOn && new Date(lastUpdatedOn), [lastUpdatedOn]);

  const updateElapsedMinutes = useCallback(() => {
    if (!lastUpdate) return;
    const minutes = Math.floor((Math.abs(Date.now() - lastUpdate) / 1000) / 60);
    setMinutesElapsed(minutes);
  }, [lastUpdate]);

  useEffect(updateElapsedMinutes, [lastUpdate]);
  useInterval(updateElapsedMinutes, lastUpdate ? (1000 * 30) : null);

  const [callback, setCallback] = useState(null);
  const [holdCallbackFiring, setHoldCallbackFiring] = useState(false);

  const handleTakeover = () => {
    checkStatus();
    setCallback(() => onTakeover);
    setHoldCallbackFiring(true); // put the callback firing on hold until the status check is complete
  };

  const handleDiscard = () => {
    checkStatus();
    setCallback(() => onDiscard);
    setHoldCallbackFiring(true); // put the callback firing on hold until the status check is complete
  };

  useEffect(() => {
    if (callback && holdCallbackFiring) setHoldCallbackFiring(false);
  }, [lastCheckedOn]);

  useEffect(() => {
    if (callback && !holdCallbackFiring && (isAdmin || canTakeAction || !recordIsLocked)) {
      callback();
      setCallback(null);
    }
  }, [holdCallbackFiring]);

  return (
    <div className="row">
      <div className="col">
        <Alert variant="success fade show">
          <h3>
            <i className="far fa-user-lock mr-2" />
            There are unsaved changes available.
          </h3>
          {displayActions && (
            <>
              <p>Do you wish to take over these changes, or discard them entirely?</p>
              <Button name="btnRowLockTakeOver" variant="primary" onClick={handleTakeover}>
                <i className="fas fa-lock mr-2" aria-hidden="true" />
                Take Over Changes
              </Button>
              <Button name="btnRemoveLock" variant="primary" onClick={handleDiscard}>
                <i className="fas fa-lock-open mr-2" aria-hidden="true" />
                Discard Changes
              </Button>
            </>
          )}
        </Alert>
      </div>
    </div>
  );
};

AutoSaveMessage.propTypes = {
  rowLockEntity: PropTypes.shape({
    type: PropTypes.string,
    id: PropTypes.number,
  }),
  isAdmin: PropTypes.bool,
  recordIsLocked: PropTypes.bool,
  checkStatus: PropTypes.func,
  onTakeover: PropTypes.func,
  onDiscard: PropTypes.func,
  displayActions: PropTypes.bool,
  lastUpdatedOn: PropTypes.string,
  lastCheckedOn: PropTypes.number,
};

export default AutoSaveMessage;
